/**
 * The allowance receipt — a "?" that answers "how was this worked out?".
 *
 * Sits next to a balance (My Leave, dashboard header card) and, on open,
 * fetches GET /leave/allowances/explain and renders the working line by line:
 * base -> service bonus -> pro-rata -> granted, + bank-holiday pot,
 * - bank holidays landing, + carry-over, - used, - pending => remaining.
 *
 * Every line renders in the OWNER's display unit via the shared formatter
 * (leaveUnits is the only place a balance becomes text). Day-denominated
 * ingredients get their minutes from days x day_minutes_basis so hours mode
 * reads consistently.
 *
 * The receipt NARRATES the stored balance; it never recomputes it. If the
 * server flags the row as drifted/stale, we say so quietly instead of
 * showing numbers that don't match the card next to it.
 */
import React, { useState } from 'react';
import { HelpCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { leaveApi } from '../lib/api';
import { formatLeave, trimDays } from '../lib/leaveUnits';
import { formatDateGB } from '../lib/dates';

const Line = ({ label, note, value, sign, bold, muted }) => (
  <div className={`flex items-start justify-between gap-3 py-1 ${bold ? 'font-semibold' : ''}`}>
    <div className="min-w-0">
      <p className={`text-xs ${bold ? '' : 'text-foreground'}`}>{label}</p>
      {note && <p className="text-[11px] text-muted-foreground">{note}</p>}
    </div>
    <p className={`text-xs whitespace-nowrap ${muted ? 'text-muted-foreground' : ''} ${
      sign === '-' ? 'text-red-500' : sign === '+' ? 'text-green-600' : ''}`}>
      {sign && <span>{sign} </span>}{value}
    </p>
  </div>
);

// `withWords` (Rob C + D, 01/11/2026): "? How is this worked out?" in orange, for the navy balance
// boxes — the grey "?" alone all but vanished on navy. Same button, same box, same testid.
export default function AllowanceExplainer({ userId, className = '', withWords = false }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (data || loading) return;
    setLoading(true);
    try {
      const r = await leaveApi.explainAllowance(userId ? { user_id: userId } : {});
      setData(r.data);
    } catch (e) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // One formatter for every line: days + (days x basis) minutes unless the
  // line carries native minutes (the bank-holiday ones do).
  const fmt = (days, minutes) => {
    if (!data) return '';
    const basis = data.day_minutes_basis;
    const mins = (minutes !== undefined && minutes !== null)
      ? minutes
      : (basis && days !== null && days !== undefined ? Math.round(days * basis) : null);
    return formatLeave(days, { unit: data.display_unit, minutes: mins });
  };

  const body = () => {
    if (failed) {
      return <p className="p-4 text-xs text-muted-foreground">Couldn't load the breakdown just now.</p>;
    }
    if (!data) {
      return (
        <div className="p-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Working it out…
        </div>
      );
    }

    const s = data.stored;
    const bh = data.bank_holidays;
    const pr = data.pro_rata;
    const basis = data.day_minutes_basis;
    const grantedNote = pr.applies
      ? `${trimDays(data.base.days + data.service_bonus.days)} × ${pr.days_employed}/${pr.days_in_year}, to the nearest half day`
      : null;

    return (
      <div className="p-3">
        <p className="text-sm font-semibold mb-1">How this was worked out</p>
        <p className="text-[11px] text-muted-foreground mb-2">
          Leave year {data.leave_year.start.slice(0, 4) === data.leave_year.end.slice(0, 4)
            ? data.leave_year.start.slice(0, 4)
            : `${data.leave_year.start.slice(0, 4)}–${data.leave_year.end.slice(0, 4)}`}
        </p>

        <div className="divide-y divide-border/60">
          <Line
            label="Base allowance"
            note={data.base.source === 'company_default' ? 'Company default' : 'Set individually'}
            value={fmt(data.base.days)}
          />
          {data.service_bonus.days > 0 && (
            <Line
              label="Length-of-service bonus"
              note={data.service_bonus.years_of_service != null
                ? `${data.service_bonus.years_of_service} completed year${data.service_bonus.years_of_service === 1 ? '' : 's'} of service`
                : null}
              value={fmt(data.service_bonus.days)}
              sign="+"
            />
          )}
          {pr.applies && (
            <Line
              label={pr.end_date ? 'Pro-rata for their employment window' : 'Pro-rata for starting mid-year'}
              note={`${pr.start_date ? `Started ${formatDateGB(pr.start_date)}` : ''}${pr.end_date ? `${pr.start_date ? ', ' : ''}leaving ${formatDateGB(pr.end_date)}` : ''} — ${pr.days_employed} of ${pr.days_in_year} days of the leave year`}
              value={`× ${trimDays(Math.round(pr.factor * 1000) / 10)}%`}
            />
          )}
          <Line label="Granted" note={grantedNote} value={fmt(data.granted_days)} bold />

          {bh.model === 'on_top' && bh.pot_minutes > 0 && (
            <Line
              label="Bank holidays"
              /* ⚠️ THE NOTE HAS TO MATCH THE ARRANGEMENT THE COMPANY CHOSE (step 7).
                 "Your share of 8" is only true under `equal_share`; under the usual
                 arrangement the number IS the ones that fall on their working days, and
                 calling it a share of eight would be a plain lie about a leave figure. */
              note={bh.part_time_model === 'equal_share'
                ? `Your share of ${bh.count} bank holiday${bh.count === 1 ? '' : 's'}`
                : `${bh.landing_count} of ${bh.count} fall on days you work`}
              value={fmt(bh.pot_days, bh.pot_minutes)}
              sign="+"
            />
          )}
          <Line label="Total allowance" value={fmt(s.total_allowance, s.total_allowance_minutes)} bold />

          {/* Carry-over expiry (DESIGN-rollover.md, 09/10/2026). Once the use-by date has
              passed, the row's carried-over figure is what's LEFT; the breakdown shows what
              came over and, on its own line, what expired — the one place it is still said
              after the date (Rob: not on the card), so the balance always adds up. */}
          {(() => {
            const ce = data.carry_expiry;
            const expired = ce && ce.state === 'expired';
            const cDays = expired ? ce.carried_days : s.carried_over;
            const cMin = expired ? ce.carried_minutes : s.carried_over_minutes;
            return (
              <>
                {(cDays || 0) !== 0 && (
                  <Line label="Carried over from last year"
                    note={ce && ce.state === 'due' ? `To be used by ${formatDateGB(ce.use_by)}` : null}
                    value={fmt(cDays, cMin)} sign="+" />
                )}
                {expired && (
                  <Line label="Carried-over leave that expired"
                    note={`Not taken by ${formatDateGB(ce.expired_on)}`}
                    value={fmt(ce.expired_days, ce.expired_minutes)} sign="-" />
                )}
              </>
            );
          })()}
          {(bh.landing_minutes || 0) > 0 && (
            <Line
              label="Bank holidays taken off automatically"
              note={bh.model === 'included'
                ? `${bh.landing_count} landing on your working days (included in your allowance)`
                : `${bh.landing_count} landing on your working days`}
              value={fmt(bh.landing_days, bh.landing_minutes)}
              sign="-"
            />
          )}
          <Line label="Leave used" value={fmt(s.used, s.used_minutes)} sign="-" />
          {(s.pending || 0) > 0 && (
            <Line label="Awaiting approval" value={fmt(s.pending, s.pending_minutes)} sign="-" />
          )}
          <Line label="Remaining" value={fmt(s.remaining, s.remaining_minutes)} bold />
        </div>

        {(!data.reconciles || data.stale) && (
          <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-500/30 p-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
            {/* ⚠️ THE COPY CHANGED ON 07/08/2026, AND SO DID WHAT IT IS ADMITTING.
                It used to say "an admin re-saving this employee will refresh it", which
                Rob rightly called out: *"can't the system do this rather than an admin
                having to re-save it? Seems a bit manual."* It was manual because nothing
                re-priced on a company save — that is now fixed, so this banner should be
                rare, and when it does appear it should say WHAT drifted rather than hand
                the reader a chore. `stale_reason` comes from the server. */}
            <p className="text-[11px] text-muted-foreground">
              {!data.reconciles
                ? "These numbers don't quite add up. Saving anything on this employee will "
                  + 'rebuild them — worth telling us if it keeps happening.'
                : `This balance predates a recent change${
                    data.stale_reason ? ` (${data.stale_reason})` : ''
                  } and hasn't caught up yet. Changing a company or location setting `
                  + 'refreshes everyone automatically; this one can be nudged by opening '
                  + 'the employee and saving.'}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <Popover onOpenChange={(open) => { if (open) load(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="How was this worked out?"
          data-testid="allowance-explainer"
          className={withWords
            ? `inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-hive-orange hover:text-hive-orange-light hover:underline underline-offset-2 transition-colors ${className}`
            : `text-muted-foreground hover:text-foreground transition-colors align-middle ${className}`}
        >
          <HelpCircle className={withWords ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
          {withWords && 'How is this worked out?'}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        {body()}
      </PopoverContent>
    </Popover>
  );
}
